import { createAsyncThunk } from "@reduxjs/toolkit";
import BulkImageAnalysisV2ApiService, {
  BiaApiError,
  toFriendlyBiaError,
} from "../apiServices/BulkImageAnalysisV2ApiService";
import {
  BiaConfig,
  BiaConfirmUploadsRs,
  BiaCreateItemRq,
  BiaCreateProductsRs,
  BiaImage,
  BiaImagePage,
  BiaPayloadItem,
  BiaPayloadPage,
  BiaPlan,
  BiaPlanBody,
  BiaPlanExport,
  BiaPlanExportFormat,
  BiaPlanVersionInfo,
  BiaRejection,
  BiaSession,
  BiaSessionFilter,
  BiaSessionListItem,
  BiaUploadFileRq,
  BiaUploadUrlsRs,
  CreateBiaSessionForm,
  UpdateBiaImageForm,
  UpdateBiaSessionForm,
} from "../../interfaces/BulkImageAnalysisV2Object";
import { BIA_CLIENT_ERROR, isRetryableBiaCode } from "../helpers/BulkImageAnalysisV2Errors";

/**
 * Async thunks for Smart photos (bulk image analysis v2).
 *
 * Chain: api service -> unwrapEnvelope / BiaApiError -> converter -> thunk ->
 * slice (dataManagement/uiDb/BulkImageAnalysisV2Db.ts).
 *
 * Every thunk rejects with a BiaRejection (friendly `message` plus the error
 * `code`, HTTP status, `retryable` and the envelope's `additionalDetails`), so a
 * component's `.unwrap()` catch can branch on `code` — see asBiaRejection. The
 * slice's errorMap keeps `message`.
 *
 * Retry: only GET thunks retry (inside the api service). Action thunks never
 * repeat themselves. Callers that poll pass `silent: true`, which keeps loading /
 * error flags untouched and skips a request while the previous silent one is
 * still in flight.
 *
 * Every ACTION thunk (plan, revise, confirm, generate, ...) resolves with the
 * fresh BiaSession; photos, plans and payloads have their own fetch thunks.
 */

type ThunkConfig = { rejectValue: BiaRejection };

/** Rejection from anything thrown by the api service. */
export function toBiaRejection(error: unknown): BiaRejection {
  if (error instanceof BiaApiError) {
    return {
      message: toFriendlyBiaError(error),
      code: error.errorCode,
      httpStatus: error.httpStatus,
      retryable: error.retryable,
      details: error.additionalDetails ?? null,
    };
  }
  const aborted = error instanceof DOMException && error.name === "AbortError";
  const code = aborted ? BIA_CLIENT_ERROR.ABORTED : "CLIENT_ERROR";
  return {
    message: toFriendlyBiaError(error),
    code,
    httpStatus: 0,
    retryable: isRetryableBiaCode(code),
    details: null,
  };
}

/** Normalises whatever `.unwrap()` threw into a BiaRejection. */
export function asBiaRejection(error: unknown): BiaRejection {
  if (
    error &&
    typeof error === "object" &&
    typeof (error as BiaRejection).message === "string" &&
    typeof (error as BiaRejection).code === "string" &&
    "httpStatus" in (error as object)
  ) {
    return error as BiaRejection;
  }
  return toBiaRejection(error);
}

/** The session an error is about (SESSION_ALREADY_ACTIVE carries it), or null. */
export function biaRejectionSessionId(rejection: BiaRejection): string | null {
  const id = rejection.details?.sessionId;
  return typeof id === "string" && id !== "" ? id : null;
}

/** INVALID_PLAN's `problems` as plain lines. */
export function biaRejectionProblems(rejection: BiaRejection): string[] {
  const raw = rejection.details?.problems;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((p) => {
      if (typeof p === "string") return p;
      if (p && typeof p === "object") {
        const rec = p as Record<string, unknown>;
        const text = rec.message ?? rec.detail ?? rec.description;
        return typeof text === "string" ? text : "";
      }
      return "";
    })
    .filter((line) => line !== "");
}

/** Silent (polling) requests in flight, so a slow response never stacks a second one on top. */
const silentInFlight = new Set<string>();

async function runSilently<T>(key: string, silent: boolean | undefined, run: () => Promise<T>): Promise<T> {
  if (!silent) return run();
  silentInFlight.add(key);
  try {
    return await run();
  } finally {
    silentInFlight.delete(key);
  }
}

const API = BulkImageAnalysisV2ApiService;

/** Defines an action thunk: one api call that resolves with the fresh session. */
function sessionAction<Arg>(type: string, run: (arg: Arg) => Promise<BiaSession>) {
  return createAsyncThunk<BiaSession, Arg, ThunkConfig>(`bulkImageAnalysisV2/${type}`, async (arg, { rejectWithValue }) => {
    try {
      return await run(arg);
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  });
}

// ── Config, list, session ────────────────────────────────────────────────────

/** Limits and the runtime switch. */
export const fetchBiaConfig = createAsyncThunk<BiaConfig, void, ThunkConfig>(
  "bulkImageAnalysisV2/fetchConfig",
  async (_arg, { rejectWithValue }) => {
    try {
      return await API.getConfig();
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  }
);

export interface BiaListQuery {
  businessId: string;
  filter: BiaSessionFilter;
}

/** Sessions of a business (ACTIVE = resume). */
export const fetchBiaSessions = createAsyncThunk<BiaSessionListItem[], BiaListQuery, ThunkConfig>(
  "bulkImageAnalysisV2/fetchSessions",
  async ({ businessId, filter }, { rejectWithValue }) => {
    try {
      return await API.listSessions(businessId, filter);
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  }
);

export const createBiaSession = createAsyncThunk<BiaSession, CreateBiaSessionForm, ThunkConfig>(
  "bulkImageAnalysisV2/createSession",
  async (form, { rejectWithValue }) => {
    try {
      return await API.createSession(form);
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  }
);

export interface BiaSessionQuery {
  sessionId: string;
  /** Polling: no loading / error flags, and skipped while the previous silent call is in flight. */
  silent?: boolean;
}

/** One session with progress and counters. `silent` for polling. */
export const fetchBiaSession = createAsyncThunk<BiaSession, BiaSessionQuery, ThunkConfig>(
  "bulkImageAnalysisV2/fetchSession",
  async ({ sessionId, silent }, { rejectWithValue }) => {
    try {
      return await runSilently(`session_${sessionId}`, silent, () => API.getSession(sessionId));
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  },
  { condition: ({ sessionId, silent }) => !(silent && silentInFlight.has(`session_${sessionId}`)) }
);

export const updateBiaSession = sessionAction<{ sessionId: string; form: UpdateBiaSessionForm }>(
  "updateSession",
  ({ sessionId, form }) => API.updateSession(sessionId, form)
);

export const cancelBiaSession = sessionAction<string>("cancelSession", (sessionId) => API.cancelSession(sessionId));

// ── Photos ───────────────────────────────────────────────────────────────────

/** Every photo of a session (all pages). */
export const fetchBiaImages = createAsyncThunk<BiaImage[], string, ThunkConfig>(
  "bulkImageAnalysisV2/fetchImages",
  async (sessionId, { rejectWithValue }) => {
    try {
      const all: BiaImage[] = [];
      let cursor: string | null = null;
      // A runaway cursor must not loop forever: 300 photos is a handful of pages.
      for (let page = 0; page < 40; page++) {
        const result: BiaImagePage = await API.listImages(sessionId, cursor);
        all.push(...result.items);
        if (!result.nextCursor || result.nextCursor === cursor) break;
        cursor = result.nextCursor;
      }
      return all;
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  }
);

export interface BiaUploadUrlsArg {
  sessionId: string;
  files: BiaUploadFileRq[];
}

export const requestBiaUploadUrls = createAsyncThunk<BiaUploadUrlsRs, BiaUploadUrlsArg, ThunkConfig>(
  "bulkImageAnalysisV2/requestUploadUrls",
  async ({ sessionId, files }, { rejectWithValue }) => {
    try {
      return await API.requestUploadUrls(sessionId, files);
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  }
);

export interface BiaConfirmUploadsArg {
  sessionId: string;
  imageIds: string[];
}

export const confirmBiaUploads = createAsyncThunk<BiaConfirmUploadsRs, BiaConfirmUploadsArg, ThunkConfig>(
  "bulkImageAnalysisV2/confirmUploads",
  async ({ sessionId, imageIds }, { rejectWithValue }) => {
    try {
      return await API.confirmUploads(sessionId, imageIds);
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  }
);

export interface BiaPatchImageArg {
  sessionId: string;
  imageId: string;
  form: UpdateBiaImageForm;
}

export interface BiaPatchImageResult {
  imageId: string;
  /** The server's image when it sent one; otherwise the slice applies `form` itself. */
  image: BiaImage | null;
  form: UpdateBiaImageForm;
  /** The session after the change (counters, planStale). */
  session: BiaSession;
}

/** TODO-BIA-SIMPLE: no UI dispatches this yet; it backs exclude/include photo in the deep-analysis view. */
export const patchBiaImage = createAsyncThunk<BiaPatchImageResult, BiaPatchImageArg, ThunkConfig>(
  "bulkImageAnalysisV2/patchImage",
  async ({ sessionId, imageId, form }, { rejectWithValue }) => {
    try {
      const { image, session } = await API.patchImage(sessionId, imageId, form);
      return { imageId, image, form, session };
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  }
);

export interface BiaDeleteImageArg {
  sessionId: string;
  imageId: string;
}

export interface BiaDeleteImageResult {
  imageId: string;
  session: BiaSession;
}

export const deleteBiaImage = createAsyncThunk<BiaDeleteImageResult, BiaDeleteImageArg, ThunkConfig>(
  "bulkImageAnalysisV2/deleteImage",
  async ({ sessionId, imageId }, { rejectWithValue }) => {
    try {
      const session = await API.deleteImage(sessionId, imageId);
      return { imageId, session };
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  }
);

// ── Plan ─────────────────────────────────────────────────────────────────────

export const startBiaPlan = sessionAction<string>("startPlan", (sessionId) => API.startPlan(sessionId));

export interface BiaPlanQuery {
  sessionId: string;
  /** Omit for the current plan. */
  version?: number;
}

export const fetchBiaPlan = createAsyncThunk<BiaPlan, BiaPlanQuery, ThunkConfig>(
  "bulkImageAnalysisV2/fetchPlan",
  async ({ sessionId, version }, { rejectWithValue }) => {
    try {
      return await API.getPlan(sessionId, version);
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  }
);

export const fetchBiaPlanVersions = createAsyncThunk<BiaPlanVersionInfo[], string, ThunkConfig>(
  "bulkImageAnalysisV2/fetchPlanVersions",
  async (sessionId, { rejectWithValue }) => {
    try {
      return await API.listPlanVersions(sessionId);
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  }
);

export const reviseBiaPlan = sessionAction<{ sessionId: string; instruction: string; baseVersion: number }>(
  "revisePlan",
  ({ sessionId, instruction, baseVersion }) => API.revisePlan(sessionId, instruction, baseVersion)
);

export const saveBiaPlan = sessionAction<{ sessionId: string; baseVersion: number; body: BiaPlanBody }>(
  "savePlan",
  ({ sessionId, baseVersion, body }) => API.savePlan(sessionId, baseVersion, body)
);

export const restoreBiaPlan = sessionAction<{ sessionId: string; version: number; baseVersion: number }>(
  "restorePlan",
  ({ sessionId, version, baseVersion }) => API.restorePlan(sessionId, version, baseVersion)
);

export interface BiaExportArg {
  sessionId: string;
  format: BiaPlanExportFormat;
}

/** Export for sharing. Not cached in the slice: it is a snapshot for the dialog that asked. */
export const exportBiaPlan = createAsyncThunk<BiaPlanExport, BiaExportArg, ThunkConfig>(
  "bulkImageAnalysisV2/exportPlan",
  async ({ sessionId, format }, { rejectWithValue }) => {
    try {
      return await API.exportPlan(sessionId, format);
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  }
);

export const confirmBiaPlan = sessionAction<{ sessionId: string; version: number }>(
  "confirmPlan",
  ({ sessionId, version }) => API.confirmPlan(sessionId, version)
);

export const unconfirmBiaPlan = sessionAction<{ sessionId: string; deleteEmptyCreatedSections: boolean }>(
  "unconfirmPlan",
  ({ sessionId, deleteEmptyCreatedSections }) => API.unconfirmPlan(sessionId, deleteEmptyCreatedSections)
);

// ── Generate, payload, create ────────────────────────────────────────────────

export const startBiaGenerate = sessionAction<string>("startGenerate", (sessionId) => API.startGenerate(sessionId));

export const retryBiaGenerate = sessionAction<{ sessionId: string; productIds?: string[] }>(
  "retryGenerate",
  ({ sessionId, productIds }) => API.retryGenerate(sessionId, productIds)
);

export interface BiaPayloadResult {
  items: BiaPayloadItem[];
  total: number;
}

/** Every generated product of a session (all pages, tree order). */
export const fetchBiaPayload = createAsyncThunk<BiaPayloadResult, string, ThunkConfig>(
  "bulkImageAnalysisV2/fetchPayload",
  async (sessionId, { rejectWithValue }) => {
    try {
      const items: BiaPayloadItem[] = [];
      let total = 0;
      let cursor: string | null = null;
      for (let page = 0; page < 60; page++) {
        const result: BiaPayloadPage = await API.getPayloadPage(sessionId, cursor, 100);
        items.push(...result.items);
        total = result.total;
        if (!result.nextCursor || result.nextCursor === cursor) break;
        cursor = result.nextCursor;
      }
      return { items, total: Math.max(total, items.length) };
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  }
);

export interface BiaCreateProductsArg {
  sessionId: string;
  items: BiaCreateItemRq[];
}

/** One chunk (<= createMaxPerCall) of the session-aware create. Never retried automatically. */
export const createBiaProducts = createAsyncThunk<BiaCreateProductsRs, BiaCreateProductsArg, ThunkConfig>(
  "bulkImageAnalysisV2/createProducts",
  async ({ sessionId, items }, { rejectWithValue }) => {
    try {
      return await API.createProducts(sessionId, items);
    } catch (error) {
      return rejectWithValue(toBiaRejection(error));
    }
  }
);

export const finishBiaSession = sessionAction<string>("finishSession", (sessionId) => API.finishSession(sessionId));
